import { Link } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';

/**
 * Public-facing Privacy Policy.
 *
 * Hosted at /legal/privacy (alias /privacy). Must remain reachable without
 * authentication so external reviewers (e.g. Intuit / QBO production-app
 * onboarding, counsel, regulators, customers, vendors) can fetch a stable
 * URL.
 *
 * To update: edit the constants below for dates / contact email, and edit
 * the JSX body. Bump POLICY_VERSION whenever the substantive terms change.
 */
const LAST_UPDATED = 'October 21, 2026';
const POLICY_VERSION = 'v1.2';
const SUPPORT_EMAIL = 'Support@virrolife.com';

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" aria-label="Virro Atlas">
            <Logo variant="primary" tone="humus" className="h-7" />
          </Link>
          <Link
            to="/auth/sign-in"
            className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Sign in
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 py-12 leading-body">
        <div className="space-y-3 border-b border-border pb-8">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Legal · {POLICY_VERSION}
          </p>
          <h1 className="text-4xl font-medium tracking-headline leading-headline">
            Virro Atlas — Privacy Policy
          </h1>
          <dl className="grid grid-cols-2 gap-2 pt-2 text-sm sm:max-w-md">
            <div>
              <dt className="font-medium text-foreground">Last Updated</dt>
              <dd className="text-muted-foreground">{LAST_UPDATED}</dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">Contact</dt>
              <dd className="text-muted-foreground">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-atmosphere-dark underline-offset-4 hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
              </dd>
            </div>
          </dl>
        </div>

        <Notice>
          <p>
            This policy explains what information{' '}
            <strong>Virro Atlas</strong> (&ldquo;Virro Atlas,&rdquo;
            &ldquo;Atlas,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) collects,
            why we collect it, who we share it with, and the choices you have.
            Virro Atlas is the internal enterprise resource planning
            (&ldquo;<strong>ERP</strong>&rdquo;) system operated by Virro for
            use by Virro personnel and authorized contractors. It is not a
            public product. If you have questions, email{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-atmosphere-dark underline-offset-4 hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          <p>
            This policy applies to two groups of people whose information may
            flow through Atlas:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Atlas users</strong> — Virro employees, officers, and
              authorized contractors who sign in to use the system.
            </li>
            <li>
              <strong>Third parties whose data is stored in Atlas</strong> —
              Virro customers, leads, vendors, suppliers, distributors, job
              applicants, and other business contacts whose information is
              captured in our CRM, sales, manufacturing, and financial
              modules.
            </li>
          </ul>
          <p>Where a section applies only to one group, we say so.</p>
        </Notice>

        <Section number="1" title="Information we collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>Account and authentication data (Atlas users).</strong>{' '}
              When a Virro administrator provisions your account we collect
              your name, work email address, role, team, and authentication
              credentials. We log every sign-in (timestamp, IP address, user
              agent) and every multi-factor authentication event.
            </li>
            <li>
              <strong>Google user data (Atlas users).</strong> Atlas
              authenticates users with Google Workspace. When you sign in,
              Google provides Atlas with the name, email address, language,
              and unique Google account identifier of the Google account you
              choose. If you later connect Google Calendar, Atlas receives
              calendar events and related metadata needed to show and
              update that calendar in Atlas. If you save a bill of materials
              to Google Sheets, you sign in with Google, choose a folder in
              your Google Drive, and choose a title. Atlas creates that
              spreadsheet in the folder you picked. The access lasts only
              for that export in your browser: Atlas does not read the rest
              of your Drive, and it does not keep a copy of the spreadsheet
              or the access token. If an administrator connects Google
              Analytics, Google Ads, Search Console, or YouTube, Atlas
              receives performance and property data for Virro&rsquo;s own
              Google marketing accounts — not Gmail contents.
            </li>
            <li>
              <strong>Activity and audit data (Atlas users).</strong> Atlas
              keeps a detailed audit trail of actions taken inside the
              system — record creations, edits, deletions, exports, document
              uploads, approvals, and permission changes — including who took
              the action, when, and from what IP address. This is required
              for financial integrity, internal controls, and compliance.
            </li>
            <li>
              <strong>
                Business records you enter or upload (Atlas users).
              </strong>{' '}
              Anything you put into Atlas — sales orders, quotes, invoices,
              customer notes, vendor records, bills of materials, work
              orders, inventory adjustments, projections, photos,
              attachments, internal comments, etc.
            </li>
            <li>
              <strong>Customer and lead data (third parties).</strong> When
              customers, prospects, or leads interact with Virro — through
              our websites, marketing forms, sales calls, emails, support
              tickets, or purchases — their information may be entered into
              Atlas. This typically includes name, email, phone, billing and
              shipping address, company name, order history, payment status,
              communication history, and any notes our team adds.
            </li>
            <li>
              <strong>
                Vendor, supplier, and contractor data (third parties).
              </strong>{' '}
              Business contact information for the people we buy from or
              contract with: name, role, company, email, phone, payment
              details we need to pay them (e.g., bank routing details for
              ACH, W-9 information), and contract terms.
            </li>
            <li>
              <strong>
                Job applicant data (third parties), if applicable.
              </strong>{' '}
              If we use Atlas to track hiring, we may store applicant name,
              contact info, resume, interview notes, and hiring decisions.
            </li>
            <li>
              <strong>Financial data.</strong> Order totals, invoice and
              payment status, tax IDs, banking details for payouts and bill
              payments, and integration data flowing to and from our
              accounting system.
            </li>
            <li>
              <strong>Manufacturing data.</strong> BOMs, routings, work
              orders, inventory ledger entries, lot and serial numbers,
              supplier lead times, quality records, and related operational
              data.
            </li>
            <li>
              <strong>Usage data.</strong> Standard server logs (IP address,
              user agent, request paths, response codes, timestamps)
              generated by our hosting provider, database provider, and edge
              functions so we can keep the system running, investigate
              issues, and detect abuse. We retain raw server logs for up to
              90 days; the in-app audit trail is retained much longer (see
              Section 6).
            </li>
            <li>
              <strong>Cookies and local storage.</strong> Atlas uses cookies
              and local browser storage to keep you signed in, remember UI
              preferences, and cache data for performance.
            </li>
          </ul>
        </Section>

        <Section number="2" title="How we use information">
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>To operate the ERP.</strong> Run sales, manufacturing,
              fulfillment, accounting, and reporting workflows; sync data
              between modules; render dashboards.
            </li>
            <li>
              <strong>To authenticate you and connect Google products.</strong>{' '}
              Atlas uses Google account identity to sign you in and apply
              your Atlas role. Atlas uses connected Google Calendar, Drive,
              Sheets, Analytics, Ads, Search Console, and YouTube data only to
              provide those Atlas features to authorized Virro users. Atlas
              does not sell Google user data, does not use it to serve ads,
              and does not use it to train generalized AI models.
            </li>
            <li>
              <strong>To manage the business.</strong> Quote, sell, invoice,
              ship, restock, pay vendors, pay taxes, close the books, run
              forecasts, and generate management reports.
            </li>
            <li>
              <strong>To support customers and vendors.</strong> Respond to
              customer questions, fulfill orders, handle returns and warranty
              claims, and manage supplier relationships.
            </li>
            <li>
              <strong>To improve the product.</strong> Debug errors, monitor
              performance, refine workflows, and develop new Atlas features.
            </li>
            <li>
              <strong>To enforce internal controls and security.</strong>{' '}
              Detect unauthorized access, investigate suspicious activity,
              prevent fraud, and uphold separation of duties.
            </li>
            <li>
              <strong>To meet legal and regulatory obligations.</strong>{' '}
              Maintain books and records, respond to lawful requests, comply
              with tax law, and meet financial-reporting requirements.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> use Atlas data to send marketing email
            to customers or leads directly out of Atlas. Marketing
            communications to customers and prospects are governed by the
            privacy notices of the customer-facing products that collected
            the data (e.g., Virro Life, Virro Waypoint) and are sent from our
            marketing tooling, not from Atlas.
          </p>
        </Section>

        <Section
          number="3"
          title="AI features and third-party AI processing"
        >
          <p>
            Atlas may include AI-assisted features — for example, drafting
            customer reply emails, summarizing long communication threads,
            classifying support tickets, generating manufacturing or sales
            insights, or extracting structured data from uploaded documents.
            To power these features, our backend sends the relevant data to{' '}
            <strong>Anthropic, PBC</strong> (the operator of the Claude API).
          </p>
          <p>
            What gets sent depends on the feature. As a general rule we send
            the minimum data needed for the task — for example, the body of
            the email being drafted, the thread being summarized, or the line
            items being analyzed. When the feature involves a customer or
            vendor record, the third party&rsquo;s name and the message
            content may be included; when it doesn&rsquo;t, we strip personal
            identifiers before sending.
          </p>
          <p>
            Anthropic processes this data on our behalf as a sub-processor
            and, under the commercial Anthropic API terms we use,{' '}
            <strong>does not train its models on this data</strong>. AI
            outputs are generated automatically and can occasionally be
            inaccurate — Atlas users are expected to review AI-generated
            drafts, summaries, and suggestions before relying on them,
            especially for anything customer-facing or financially
            significant.
          </p>
          <p>
            If we add new AI features that send materially different
            categories of data to a third party, we will update this policy.
          </p>
        </Section>

        <Section number="4" title="Other third parties we share data with">
          <p>
            We use a small set of vendors that process Atlas data on our
            behalf. They receive only the data they need for the function
            they perform, under contractual confidentiality and
            data-protection terms.
          </p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>Google LLC</strong> — identity (Google Workspace
              sign-in) and, when an Atlas user or administrator connects
              them, Google Calendar, Google Drive, Google Sheets, Google
              Analytics, Google Ads, Search Console, and YouTube. Atlas sends
              Google the OAuth tokens and API requests needed for those
              connections and receives back
              the Google user data described in Section 1. Google processes
              that data under its own terms as the operator of those
              services.
            </li>
            <li>
              <strong>Supabase</strong> — primary database, authentication,
              file storage, and serverless function hosting for Atlas.
            </li>
            <li>
              <strong>Vercel</strong> — hosting the Atlas web app.
            </li>
            <li>
              <strong>Anthropic, PBC</strong> — powering AI features inside
              Atlas (see Section 3).
            </li>
            <li>
              <strong>Resend</strong> — sending transactional and operational
              email from Atlas (order confirmations, internal notifications,
              password resets).
            </li>
            <li>
              <strong>Stripe</strong> — processing customer payments and
              storing payment-method information; Atlas reads order and
              payout data via integration but does not store full card
              numbers.
            </li>
            <li>
              <strong>QuickBooks Online</strong> — receiving categorised
              spend (Purchase records) from Atlas, and sending its chart
              of accounts plus Profit &amp; Loss and balance sheet
              reports back to Atlas for reconciliation.
            </li>
            <li>
              <strong>ShipStation</strong> — receiving shipment requests and
              returning tracking information for orders.
            </li>
            <li>
              <strong>TaxJar</strong> — calculating, collecting, and
              remitting sales tax.
            </li>
            <li>
              <strong>Error monitoring and analytics providers</strong> —
              logging crashes, performance metrics, and aggregate usage data
              so we can keep Atlas reliable.
            </li>
          </ul>
          <p>
            <strong>
              We do not sell personal information, and we do not share Atlas
              data with advertising networks or data brokers.
            </strong>{' '}
            If our practices ever change, we will update this policy and
            notify affected parties before the change takes effect.
          </p>
          <p>
            We may share information within the Virro family of products (for
            example, between Atlas and Virro Life or Virro Waypoint) so that
            we can operate as a unified business — for example, syncing
            customer records between an order placed on virrolife.com and the
            corresponding sales record in Atlas. This is internal to Virro
            and is not a sale.
          </p>
          <p>
            We may also disclose information when required by law (e.g.,
            valid legal process, subpoena, court order), to investigate fraud
            or abuse, to enforce our contracts, or to protect the rights,
            property, or safety of Virro, our users, our customers, or the
            public. We may also disclose information to professional advisors
            (lawyers, accountants, auditors) under confidentiality
            obligations, and to acquirers, investors, or successors as part
            of a financing, due-diligence process, merger, acquisition, or
            sale of assets.
          </p>
        </Section>

        <Section number="5" title="Data security">
          <p>
            We protect Atlas data using industry-standard practices:
            encrypted connections (HTTPS/TLS) for all traffic between your
            device and our servers, encryption of data at rest in our
            database and file storage, hashed passwords (we never store or
            see plaintext passwords), required multi-factor authentication
            for privileged accounts, role-based access controls so users only
            see what their role allows, separation of duties for sensitive
            financial actions, and a complete audit trail of administrative
            and financial activity. No system is perfectly secure, so we
            cannot guarantee absolute security — but we work hard to protect
            Atlas data and we will notify affected parties promptly if we
            ever experience a breach that requires it under applicable law.
          </p>
          <p>
            Atlas users are responsible for keeping their credentials
            confidential, using multi-factor authentication, locking their
            devices, and reporting suspected security issues to{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-atmosphere-dark underline-offset-4 hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>{' '}
            immediately.
          </p>
        </Section>

        <Section number="6" title="Data retention">
          <p>
            Atlas holds the operational and financial records of the
            business, so retention here is longer than for our consumer
            products.
          </p>
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong>Active records.</strong> Customer, vendor, order,
              invoice, manufacturing, and inventory records are retained
              while they are operationally relevant.
            </li>
            <li>
              <strong>Financial and tax records.</strong> We retain financial
              records (invoices, bills, payments, tax filings, journal
              entries, audit trail) for at least{' '}
              <strong>seven (7) years</strong> after the close of the
              relevant tax year, or longer if required by applicable law, to
              meet tax, accounting, and audit obligations.
            </li>
            <li>
              <strong>Audit trail.</strong> The in-app audit trail of user
              actions is retained for at least{' '}
              <strong>seven (7) years</strong>.
            </li>
            <li>
              <strong>Server logs.</strong> Raw server logs are retained for
              up to <strong>90 days</strong>.
            </li>
            <li>
              <strong>User accounts.</strong> When an Atlas user leaves
              Virro or no longer needs access, their account is deactivated
              promptly. Their identity remains attached to historical records
              in the audit trail (so we know who took which action) but they
              can no longer sign in.
            </li>
            <li>
              <strong>Deletion requests from third parties.</strong>{' '}
              Customers, vendors, and other third parties whose data appears
              in Atlas may request deletion (see Section 7). Where we are
              legally required to retain a record (e.g., a paid invoice for
              tax purposes), we will retain only what is required and
              restrict access to it.
            </li>
            <li>
              <strong>Backups.</strong> Encrypted backups containing deleted
              data are purged on a rolling basis (typically within 60 days
              of deletion).
            </li>
          </ul>
        </Section>

        <Section number="7" title="Your choices and rights">
          <Subsection title="7.1 For Atlas users (Virro personnel and authorized contractors)">
            <ul className="list-disc space-y-3 pl-5">
              <li>
                You can update your profile and notification preferences
                inside the app.
              </li>
              <li>
                You can request that your account be deactivated when your
                engagement with Virro ends; historical audit-trail entries
                tied to your identity will be retained as described in
                Section 6.
              </li>
              <li>
                You can request a copy of the personal information Atlas
                holds about you in your role as a user, subject to
                legitimate business and confidentiality limits.
              </li>
              <li>
                Atlas is a work tool. Information you create or upload in
                Atlas — including communications, notes, and audit-trail
                entries — is the property of Virro and may be reviewed by
                authorized Virro personnel for legitimate business reasons
                (operations, security, compliance, investigations).
              </li>
            </ul>
          </Subsection>
          <Subsection title="7.2 For third parties whose data is stored in Atlas (customers, vendors, applicants, other contacts)">
            <ul className="list-disc space-y-3 pl-5">
              <li>
                <strong>Access, correction, export, or deletion.</strong>{' '}
                You can request a copy of the personal information we hold
                about you, ask us to correct it, or ask us to delete it by
                emailing{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-atmosphere-dark underline-offset-4 hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
                . Where we are legally required to keep a record (e.g., an
                invoice for a completed transaction), we will retain only
                what is required and restrict access to it.
              </li>
              <li>
                <strong>Region-specific rights.</strong> Depending on where
                you live (e.g., EU/UK, California, Colorado, Virginia,
                Connecticut, Texas, and other U.S. states with comprehensive
                privacy laws), you may have additional rights such as
                access, correction, portability, deletion, the right to opt
                out of profiling or targeted advertising (which we do not
                perform from Atlas), and the right to lodge a complaint with
                a data protection authority. We honor those requests — email{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-atmosphere-dark underline-offset-4 hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>{' '}
                to exercise them. We will not retaliate against you for
                exercising any of these rights.
              </li>
              <li>
                <strong>Marketing.</strong> Atlas does not directly send
                marketing email. To opt out of marketing email from Virro,
                use the unsubscribe link in any marketing message, or email{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-atmosphere-dark underline-offset-4 hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
                . Opting out of marketing does not stop transactional
                communications (order confirmations, shipping updates,
                invoices, receipts) — those are required to operate the
                business relationship.
              </li>
            </ul>
          </Subsection>
        </Section>

        <Section number="8" title="Children">
          <p>
            Virro Atlas is an internal business system and is not directed
            to or intended for use by children. We do not knowingly collect
            personal information from children through Atlas. If a
            child&rsquo;s information has somehow ended up in Atlas (for
            example, on an order placed by a parent), please contact us and
            we will handle it appropriately.
          </p>
        </Section>

        <Section number="9" title="International transfers">
          <p>
            Virro Atlas operates from the United States. If you are located
            outside the U.S. and your information ends up in Atlas — for
            example, because you bought a product, supplied us with goods,
            or otherwise did business with Virro — you understand that your
            data will be transferred to and processed in the U.S. and other
            countries where our service providers operate.
          </p>
        </Section>

        <Section number="10" title="Changes to this policy">
          <p>
            We may update this policy as Atlas evolves. When we make
            material changes — including any change to whether we sell
            personal information, the categories of vendors we share data
            with, or the categories of AI processing we perform — we will
            update the &ldquo;Last updated&rdquo; date at the top of this
            page and notify Atlas users in the app or by email before the
            change takes effect. For third parties whose data is in Atlas,
            we will publish the updated policy at the same location and
            notify customers through the channel of the underlying business
            relationship where appropriate.
          </p>
        </Section>

        <Section number="11" title="Contact us">
          <p>
            Questions, concerns, or data requests:{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-atmosphere-dark underline-offset-4 hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
          <address className="not-italic">
            <p className="font-medium text-foreground">
              Eloo LLC d/b/a Virro Life
            </p>
            <p>230 Stanford Parkway</p>
            <p>Findlay, OH 45840</p>
          </address>
        </Section>
      </article>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-6 py-8 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>
            &copy; {new Date().getFullYear()} Eloo LLC d/b/a Virro Life. All
            rights reserved.
          </p>
          <p>
            Privacy Policy {POLICY_VERSION} · Last updated {LAST_UPDATED}
          </p>
        </div>
      </footer>
    </div>
  );
}

function Section({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-12 space-y-4 scroll-mt-24" id={`section-${number}`}>
      <h2 className="text-xl font-medium tracking-headline leading-headline">
        <span className="mr-2 text-muted-foreground">{number}.</span>
        {title}
      </h2>
      <div className="space-y-4 text-sm">{children}</div>
    </section>
  );
}

function Subsection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <aside
      role="note"
      className="my-6 space-y-3 rounded-lg border-l-4 border-foreground bg-secondary/60 px-5 py-4 text-sm"
    >
      {children}
    </aside>
  );
}
